import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import i18n from '@/i18n'
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@/i18n'

interface LanguageContextType {
  language: SupportedLanguage
  setLanguage: (lang: SupportedLanguage) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const getPreferredLanguage = (): SupportedLanguage => {
  // Check localStorage for a previously selected language
  const stored = localStorage.getItem('i18nextLng')
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage
  }

  // Default to English
  return 'en'
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Validate language from URL or use default
  const urlLanguage = lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
    ? lang as SupportedLanguage
    : null

  const [language, setLanguageState] = useState<SupportedLanguage>(
    urlLanguage || getPreferredLanguage()
  )

  // Sync i18n with URL language
  useEffect(() => {
    if (urlLanguage && urlLanguage !== i18n.language) {
      i18n.changeLanguage(urlLanguage)
      setLanguageState(urlLanguage)
      localStorage.setItem('i18nextLng', urlLanguage)
    }
  }, [urlLanguage])

  const setLanguage = useCallback((newLang: SupportedLanguage) => {
    if (newLang === language) return

    // Update i18n
    i18n.changeLanguage(newLang)
    setLanguageState(newLang)
    localStorage.setItem('i18nextLng', newLang)

    // Update URL by replacing the language prefix
    const currentPath = location.pathname
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, '')
    const newPath = `/${newLang}${pathWithoutLang || '/'}`

    navigate(newPath, { state: location.state, replace: true })
  }, [language, location.pathname, location.state, navigate])

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
